import { useEffect, useState } from "react";
import { Page, useCurrent } from "./common";
import { PageHead, Plate, StatusBadge, Modal, Avatar, Empty } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions } from "../lib/store";
import { storeImage } from "../lib/sync";
import { fileToDataUri } from "../lib/image";
import { ChangeRegister } from "../components/ChangeRegister";
import { downloadReviewSchedulePdf } from "../lib/pdf";
import { useCan, useSession, useIsAdmin } from "../lib/auth";
import { apiMembers, type Member } from "../lib/authApi";
import { summarizeReview } from "../lib/ai";
import { sendToStudio } from "../lib/bridge";
import { relative, fmtDate } from "../lib/format";
import type { DrawingReview, ReviewSheet } from "../lib/types";

/** A team member can see a review if: they're admin, the review has no explicit
 *  access list (open to the team), or their email is on the access list. */
function visibleTo(r: DrawingReview, email: string | undefined, admin: boolean) {
  return admin || r.access.length === 0 || (!!email && r.access.includes(email));
}

export default function Review() {
  const p = useCurrent();
  const canEdit = useCan("reviews");
  const isAdmin = useIsAdmin();
  const session = useSession();
  const [sel, setSel] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [title, setTitle] = useState("");
  if (!p) return null;

  const reviews = (p.reviews ?? []).filter((r) => visibleTo(r, session?.email, isAdmin));
  const current = reviews.find((r) => r.id === sel);

  function create() {
    if (!title.trim()) return;
    const id = actions.createReview(p!.id, title.trim());
    setTitle(""); setNewOpen(false); setSel(id);
  }

  return (
    <Page>
      {!current ? (
        <>
          <PageHead kicker={`${p.name} · Coordination`} title="Drawing review">
            {canEdit && <SyncDrawingsButton p={p} />}
            {canEdit && <button className="btn accent sm" onClick={() => setNewOpen(true)}><Icon.plus width={15} />New review</button>}
          </PageHead>
          <p className="muted reveal" style={{ maxWidth: 620, marginBottom: 22 }}>
            Share drawing sets with the client for direct, sheet-by-sheet feedback. The AI distils their remarks into a clear change summary, and revised sheets close the loop.
          </p>
          {reviews.length === 0 && <Empty>No reviews you can access yet.</Empty>}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: 18 }}>
            {reviews.map((r) => {
              const open = openRemarks(r);
              return (
                <button key={r.id} className="card hoverable reveal" onClick={() => setSel(r.id)} style={{ textAlign: "left", padding: 20, cursor: "pointer", background: "var(--card)", border: "1px solid var(--line)" }}>
                  <div className="row between"><StatusBadge status={r.status === "open" ? "active" : "done"} />{r.sharedWithClient ? <span className="badge ok" style={{ fontSize: 10 }}><span className="dot" />Shared</span> : <span className="badge" style={{ fontSize: 10 }}>Private</span>}</div>
                  <h3 className="serif" style={{ fontSize: 21, marginTop: 10 }}>{r.title}</h3>
                  <div className="dim" style={{ fontSize: 12.5, marginTop: 3 }}>{r.sheets.length} sheets · {fmtDate(r.createdOn)}</div>
                  <div className="row between" style={{ marginTop: 14 }}>
                    <span className="badge accent">{open} open remark{open !== 1 ? "s" : ""}</span>
                    {r.access.length > 0 && <span className="row dim" style={{ gap: 5, fontSize: 11.5 }}><Icon.gear width={13} />limited</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <ReviewDetail p={p} r={current} canEdit={canEdit} isAdmin={isAdmin} authorName={session?.name ?? "Studio"} onBack={() => setSel(null)} />
      )}

      <Modal open={newOpen} onClose={() => setNewOpen(false)} title="New drawing review">
        <div className="col" style={{ gap: 14 }}>
          <div className="field"><label>Title</label><input className="input" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Developed Design — Review 02" onKeyDown={(e) => e.key === "Enter" && create()} /></div>
          <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}><button className="btn ghost" onClick={() => setNewOpen(false)}>Cancel</button><button className="btn accent" onClick={create}>Create</button></div>
        </div>
      </Modal>
    </Page>
  );
}

function openRemarks(r: DrawingReview) { return r.sheets.reduce((n, s) => n + s.remarks.filter((m) => !m.resolved).length, 0); }

/** Push the drawing register and every issued drawing across to the Project
 *  Manager on site. A first send creates the site project; once linked, this
 *  re-syncs the current drawings onto the same job (matched by sheet number,
 *  auto-revisioned there). The register travels as deliverables; the pictures
 *  as the review sheets' uploaded images. */
function SyncDrawingsButton({ p }: { p: any }) {
  const pmLink = p.links?.pm;
  const sheets = (p.deliverables ?? []).length;
  const drawings = (p.reviews ?? []).reduce((n: number, r: any) => n + r.sheets.filter((s: any) => s.url).length, 0);
  const title = pmLink
    ? `Re-sync ${sheets} register sheet${sheets === 1 ? "" : "s"}${drawings ? ` + ${drawings} drawing${drawings === 1 ? "" : "s"}` : ""} to ${pmLink.code || "Project Manager"}`
    : "Create the site project in Project Manager and carry the drawings across";
  return (
    <button className="btn ghost sm" title={title} onClick={() => sendToStudio(p, "pm")}>
      <Icon.swap width={14} />{pmLink ? "Sync drawings → PM" : "Send to Project Manager"}
    </button>
  );
}

function ReviewDetail({ p, r, canEdit, isAdmin, authorName, onBack }: { p: any; r: DrawingReview; canEdit: boolean; isAdmin: boolean; authorName: string; onBack: () => void }) {
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState<Record<string, string>>({});
  const [sheetOpen, setSheetOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [respondFor, setRespondFor] = useState<{ sheetId: string; remarkId: string } | null>(null);
  const [sheet, setSheet] = useState({ code: "", title: "", discipline: "Architecture" });

  async function summarize() {
    setBusy(true);
    const res = await summarizeReview(r.title, r.sheets);
    setBusy(false);
    if (res.ok && res.summary) actions.setReviewSummary(p.id, r.id, res.summary);
  }
  function addRemark(sheetId: string) {
    const body = (reply[sheetId] ?? "").trim(); if (!body) return;
    actions.addRemark(p.id, r.id, sheetId, "team", authorName, body);
    setReply((x) => ({ ...x, [sheetId]: "" }));
  }
  function addSheet() {
    if (!sheet.code.trim()) return;
    actions.addReviewSheet(p.id, r.id, { code: sheet.code.trim(), title: sheet.title.trim() || "Untitled sheet", discipline: sheet.discipline });
    setSheet({ code: "", title: "", discipline: "Architecture" }); setSheetOpen(false);
  }

  return (
    <>
      <div className="row between wrap reveal" style={{ marginBottom: 20, gap: 12 }}>
        <div>
          <button className="btn ghost sm" onClick={onBack} style={{ marginBottom: 10 }}><Icon.back width={15} />All reviews</button>
          <div className="row" style={{ gap: 10 }}><h1 className="serif" style={{ fontSize: 34 }}>{r.title}</h1><StatusBadge status={r.status === "open" ? "active" : "done"} /></div>
          <div className="dim" style={{ fontSize: 13, marginTop: 4 }}>{r.sheets.length} sheets · {openRemarks(r)} open remarks</div>
        </div>
        {canEdit && (
          <div className="row wrap" style={{ gap: 8 }}>
            <button className="btn accent sm" onClick={summarize} disabled={busy}><Icon.spark width={15} />{busy ? "Summarising…" : "AI summary"}</button>
            <button className="btn ghost sm" title="Download the comment / response schedule in construction-drawing format" onClick={() => downloadReviewSchedulePdf(p, r)}><Icon.download width={14} />Review schedule</button>
            <button className={"btn sm " + (r.sharedWithClient ? "soft" : "ghost")} onClick={() => actions.shareReview(p.id, r.id, !r.sharedWithClient)}><Icon.arrowUpRight width={14} />{r.sharedWithClient ? "Shared with client" : "Share with client"}</button>
            {isAdmin && <button className="btn ghost sm" onClick={() => setAccessOpen(true)}><Icon.gear width={14} />Access{r.access.length ? ` (${r.access.length})` : ""}</button>}
          </div>
        )}
      </div>

      {/* AI change summary */}
      {r.summary && (
        <div className="card pad reveal" style={{ marginBottom: 22, borderColor: "color-mix(in srgb, var(--accent) 30%, var(--line))", background: "color-mix(in srgb, var(--accent) 4%, var(--card))" }}>
          <div className="row" style={{ gap: 8, marginBottom: 10 }}><Icon.spark width={16} style={{ color: "var(--accent-ink)" }} /><span className="kicker">Change summary</span></div>
          <div style={{ fontSize: 15, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{r.summary}</div>
        </div>
      )}

      {/* the coordination table — client change requests → responses */}
      <ChangeRegister r={r} mode="studio" onRespond={canEdit ? (sheetId, remarkId) => setRespondFor({ sheetId, remarkId }) : undefined} />

      {/* sheets */}
      <div className="col" style={{ gap: 18 }}>
        {r.sheets.map((s) => (
          <SheetCard key={s.id} p={p} r={r} s={s} canEdit={canEdit} reply={reply[s.id] ?? ""} onReply={(v) => setReply((x) => ({ ...x, [s.id]: v }))} onSend={() => addRemark(s.id)} onRespond={(remarkId) => setRespondFor({ sheetId: s.id, remarkId })} />
        ))}
        {canEdit && <button className="panel" onClick={() => setSheetOpen(true)} style={{ padding: 16, borderStyle: "dashed", cursor: "pointer", color: "var(--ink-3)" }}><span className="row" style={{ gap: 8, justifyContent: "center" }}><Icon.plus width={16} />Add a sheet</span></button>}
      </div>

      <Modal open={sheetOpen} onClose={() => setSheetOpen(false)} title="Add a sheet">
        <div className="col" style={{ gap: 12 }}>
          <div className="row" style={{ gap: 10 }}>
            <div className="field" style={{ width: 120 }}><label>Sheet no.</label><input className="input mono" autoFocus value={sheet.code} onChange={(e) => setSheet({ ...sheet, code: e.target.value })} placeholder="A-101" /></div>
            <div className="field" style={{ flex: 1 }}><label>Title</label><input className="input" value={sheet.title} onChange={(e) => setSheet({ ...sheet, title: e.target.value })} placeholder="Ground floor plan" /></div>
          </div>
          <div className="field"><label>Discipline</label><select className="select" value={sheet.discipline} onChange={(e) => setSheet({ ...sheet, discipline: e.target.value })}>{["Architecture", "Structural", "MEP", "Interiors", "Landscape"].map((d) => <option key={d}>{d}</option>)}</select></div>
          <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}><button className="btn ghost" onClick={() => setSheetOpen(false)}>Cancel</button><button className="btn accent" onClick={addSheet}>Add sheet</button></div>
        </div>
      </Modal>

      {isAdmin && <AccessModal open={accessOpen} onClose={() => setAccessOpen(false)} review={r} pid={p.id} />}
      {respondFor && <RespondModal p={p} r={r} sheetId={respondFor.sheetId} remarkId={respondFor.remarkId} by={authorName} onClose={() => setRespondFor(null)} />}
    </>
  );
}

/** Upload a drawing image (compressed) to storage, returning its URL. */
async function pickImage(file: File | undefined, set: (url: string) => void, setBusy: (b: boolean) => void) {
  if (!file) return;
  setBusy(true);
  try { set(await storeImage(await fileToDataUri(file))); } catch { /* leave as-is */ }
  setBusy(false);
}

/** Record the studio's response to a client change request: what changed + a
 *  photo highlighting the changed area on the drawing. */
function RespondModal({ p, r, sheetId, remarkId, by, onClose }: { p: any; r: DrawingReview; sheetId: string; remarkId: string; by: string; onClose: () => void }) {
  const s = r.sheets.find((x) => x.id === sheetId);
  const m = s?.remarks.find((x) => x.id === remarkId);
  const [body, setBody] = useState(m?.response?.body ?? "");
  const [photo, setPhoto] = useState<string | undefined>(m?.response?.url);
  const [busy, setBusy] = useState(false);
  function submit() {
    if (!body.trim()) return;
    actions.respondToRemark(p.id, r.id, sheetId, remarkId, { body: body.trim(), url: photo }, by);
    onClose();
  }
  return (
    <Modal open onClose={onClose} title="Respond to change request">
      <div className="col" style={{ gap: 14 }}>
        <div className="panel" style={{ padding: "10px 12px" }}>
          <div className="row" style={{ gap: 8 }}><span className="mono" style={{ fontWeight: 600 }}>{s?.code}</span><span className="badge" style={{ fontSize: 9 }}>rev {s?.revision}</span></div>
          <div style={{ fontSize: 13.5, marginTop: 6 }}>“{m?.body}”</div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 3 }}>{m?.authorName} · {fmtDate(m?.createdOn)}</div>
        </div>
        <div className="field"><label>What changed in response</label>
          <textarea className="input" rows={3} autoFocus value={body} onChange={(e) => setBody(e.target.value)} placeholder="e.g. Widened the entrance to 1.2m and relocated the coat store, as requested." />
        </div>
        <div className="field"><label>Photo of the changed area <span className="dim">(highlight what moved)</span></label>
          {photo && <img src={photo} alt="changed area" style={{ width: "100%", maxHeight: 220, objectFit: "cover", borderRadius: 8, marginBottom: 8, border: "1px solid var(--line)" }} />}
          <label className="btn soft sm" style={{ cursor: "pointer", width: "fit-content" }}><Icon.download width={14} style={{ transform: "rotate(180deg)" }} />{busy ? "Uploading…" : photo ? "Replace photo" : "Upload photo"}<input type="file" accept="image/*" hidden onChange={(e) => pickImage(e.target.files?.[0] ?? undefined, setPhoto, setBusy)} /></label>
        </div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn accent" disabled={!body.trim() || busy} onClick={submit}><Icon.check width={15} />Record response</button></div>
      </div>
    </Modal>
  );
}

function SheetCard({ p, r, s, canEdit, reply, onReply, onSend, onRespond }: { p: any; r: DrawingReview; s: ReviewSheet; canEdit: boolean; reply: string; onReply: (v: string) => void; onSend: () => void; onRespond: (remarkId: string) => void }) {
  const [uploadOpen, setUploadOpen] = useState(false);
  return (
    <div className="card reveal" style={{ overflow: "hidden" }}>
      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr" }} className="sheet-grid">
        <div style={{ position: "relative", borderRight: "1px solid var(--line)", minHeight: 220 }}>
          {s.url
            ? <img src={s.url} alt={`${s.code} ${s.title}`} style={{ width: "100%", height: "100%", minHeight: 220, objectFit: "cover", display: "block" }} />
            : <Plate hue={s.hue} h={220} label={`${s.code} · ${s.revision}`} />}
          {s.url && <span className="badge" style={{ position: "absolute", top: 12, left: 12, background: "rgba(255,255,255,.9)", color: "var(--ink)" }}>{s.code} · {s.revision}</span>}
          {canEdit && <button className="btn soft sm" style={{ position: "absolute", bottom: 12, right: 12 }} onClick={() => setUploadOpen(true)}><Icon.download width={14} style={{ transform: "rotate(180deg)" }} />{s.url ? "Upload revision" : "Upload drawing"}</button>}
        </div>
        <div style={{ padding: 20 }}>
          <div className="row between">
            <div><div className="row" style={{ gap: 8 }}><span className="mono" style={{ color: "var(--ink-2)" }}>{s.code}</span><span className="badge" style={{ fontSize: 10 }}>{s.discipline}</span><span className="badge accent" style={{ fontSize: 10 }}>rev {s.revision}</span></div><h3 className="serif" style={{ fontSize: 19, marginTop: 6 }}>{s.title}</h3></div>
          </div>
          {s.summary && <div className="panel" style={{ padding: "9px 12px", marginTop: 10, fontSize: 12.5, lineHeight: 1.55 }}><span className="kicker" style={{ fontSize: 8.5 }}>What changed{s.revisedOn ? ` · ${relative(s.revisedOn)}` : ""}</span><div style={{ marginTop: 3 }}>{s.summary}</div></div>}
          <div className="col" style={{ gap: 10, marginTop: 14 }}>
            {s.remarks.length === 0 && <div className="dim" style={{ fontSize: 13 }}>No remarks on this sheet.</div>}
            {s.remarks.map((m) => (
              <div key={m.id} className="panel" style={{ padding: "10px 12px", opacity: m.resolved && !m.response ? 0.6 : 1 }}>
                <div className="row between">
                  <div className="row" style={{ gap: 8 }}><Avatar name={m.authorName} hue={m.author === "client" ? p.client.avatarHue : 20} size={24} /><span style={{ fontSize: 12.5, fontWeight: 600 }}>{m.authorName}</span><span className="badge" style={{ fontSize: 9 }}>{m.author}</span><span className="dim" style={{ fontSize: 11 }}>rev {m.onRevision} · {relative(m.createdOn)}</span></div>
                  {canEdit && (
                    <div className="row" style={{ gap: 6 }}>
                      {m.author === "client" && <button className="chip" style={{ fontSize: 11, padding: "3px 9px", background: m.response ? "var(--card)" : "var(--accent)", color: m.response ? "var(--ink)" : "#fff", borderColor: m.response ? "var(--line-2)" : "var(--accent)" }} onClick={() => onRespond(m.id)}>{m.response ? "Edit response" : "Respond"}</button>}
                      <button className="chip" style={{ fontSize: 11, padding: "3px 9px" }} onClick={() => actions.resolveRemark(p.id, r.id, s.id, m.id)}>{m.resolved ? "Reopen" : "Resolve"}</button>
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 14, marginTop: 6, textDecoration: m.resolved && !m.response ? "line-through" : "none" }}>{m.body}</div>
                {m.response && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--line-2)" }}>
                    <div className="row" style={{ gap: 8, marginBottom: 6 }}><Icon.check width={13} style={{ color: "var(--ok)" }} /><span className="kicker" style={{ fontSize: 8.5 }}>Response · {m.response.by}{m.response.revision ? ` · rev ${m.response.revision}` : ""}</span></div>
                    <div style={{ fontSize: 13.5 }}>{m.response.body}</div>
                    {m.response.url && <img src={m.response.url} alt="changed area" style={{ width: 140, height: 96, objectFit: "cover", borderRadius: 8, marginTop: 8, border: "1px solid var(--line)" }} />}
                  </div>
                )}
              </div>
            ))}
          </div>
          {canEdit && (
            <div className="row" style={{ gap: 8, marginTop: 12 }}>
              <input className="input" placeholder="Reply to the client…" value={reply} onChange={(e) => onReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onSend()} />
              <button className="btn accent" onClick={onSend}><Icon.send width={15} /></button>
            </div>
          )}
        </div>
      </div>
      {uploadOpen && <SheetUploadModal p={p} rid={r.id} s={s} onClose={() => setUploadOpen(false)} />}
      <style>{`@media (max-width:720px){ .sheet-grid{ grid-template-columns:1fr !important } }`}</style>
    </div>
  );
}

/** Attach a drawing to a sheet — the first upload sets the drawing, later uploads
 *  issue a new revision with a summary of what changed. */
function SheetUploadModal({ p, rid, s, onClose }: { p: any; rid: string; s: ReviewSheet; onClose: () => void }) {
  const isRevision = !!s.url;
  const [img, setImg] = useState<string | undefined>();
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  function submit() {
    if (isRevision) actions.uploadRevision(p.id, rid, s.id, { url: img, summary });
    else actions.setSheetDrawing(p.id, rid, s.id, { url: img, summary });
    onClose();
  }
  return (
    <Modal open onClose={onClose} title={isRevision ? `Issue revised ${s.code}` : `Upload ${s.code}`}>
      <div className="col" style={{ gap: 14 }}>
        <div className="dim" style={{ fontSize: 13, lineHeight: 1.55 }}>{isRevision
          ? <>Reissues <b>{s.code}</b> at the next revision (now {s.revision}). The client sees the new drawing and your summary of the changes.</>
          : <>Attach the drawing for <b>{s.code}</b> and note what it shows.</>}</div>
        <div className="field"><label>{isRevision ? "Revised drawing" : "Drawing"}</label>
          {(img || s.url) && <img src={img || s.url} alt={s.code} style={{ width: "100%", maxHeight: 240, objectFit: "cover", borderRadius: 8, marginBottom: 8, border: "1px solid var(--line)" }} />}
          <label className="btn soft sm" style={{ cursor: "pointer", width: "fit-content" }}><Icon.download width={14} style={{ transform: "rotate(180deg)" }} />{busy ? "Uploading…" : img ? "Replace image" : "Choose image"}<input type="file" accept="image/*" hidden onChange={(e) => pickImage(e.target.files?.[0] ?? undefined, setImg, setBusy)} /></label>
        </div>
        <div className="field"><label>{isRevision ? "Summary of changes" : "What this drawing shows"}</label>
          <textarea className="input" rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder={isRevision ? "e.g. Entrance widened, coat store relocated, kitchen island revised." : "Optional note"} />
        </div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn accent" disabled={busy || (isRevision && !img && !summary.trim())} onClick={submit}><Icon.check width={15} />{isRevision ? "Issue revision" : "Save drawing"}</button></div>
      </div>
    </Modal>
  );
}

function AccessModal({ open, onClose, review, pid }: { open: boolean; onClose: () => void; review: DrawingReview; pid: string }) {
  const [members, setMembers] = useState<Member[]>([]);
  useEffect(() => { if (open) apiMembers().then(setMembers); }, [open]);
  function toggle(email: string) {
    const next = review.access.includes(email) ? review.access.filter((e) => e !== email) : [...review.access, email];
    actions.setReviewAccess(pid, review.id, next);
  }
  return (
    <Modal open={open} onClose={onClose} title="Who can access this review">
      <p className="muted" style={{ fontSize: 14, marginBottom: 14 }}>Admins always have access. Tick the team members who should see this review. Leave all unticked to open it to the whole team.</p>
      <div className="col" style={{ gap: 8 }}>
        {members.filter((m) => m.role !== "admin").length === 0 && <div className="dim" style={{ fontSize: 13 }}>No other team accounts yet.</div>}
        {members.filter((m) => m.role !== "admin").map((m) => {
          const on = review.access.includes(m.email);
          return (
            <button key={m.email} className="panel row between" onClick={() => toggle(m.email)} style={{ padding: "10px 14px", cursor: "pointer", borderColor: on ? "var(--accent)" : "var(--line)", background: on ? "var(--accent-soft)" : "var(--card)" }}>
              <span className="row" style={{ gap: 10 }}><Avatar name={m.name || m.email} hue={(m.email.charCodeAt(0) * 7) % 360} size={30} /><span><span style={{ display: "block", fontSize: 13.5, fontWeight: 500 }}>{m.name || m.email.split("@")[0]}</span><span className="dim" style={{ fontSize: 11.5 }}>{m.email}</span></span></span>
              <span style={{ width: 20, height: 20, borderRadius: 6, display: "grid", placeItems: "center", background: on ? "var(--accent)" : "transparent", border: "1.5px solid " + (on ? "var(--accent)" : "var(--line-2)"), color: "#fff" }}>{on && <Icon.check width={13} />}</span>
            </button>
          );
        })}
      </div>
      <div className="row" style={{ justifyContent: "flex-end", marginTop: 16 }}><button className="btn accent" onClick={onClose}>Done</button></div>
    </Modal>
  );
}
